import React, { useContext, useEffect, useState } from 'react';
import { PaymentPeriod } from '@klab-berlin/api-sdk/lib/types/common';
import { DiscountInformation } from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import { useForm } from 'react-hook-form';
import services from '../../../services';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import { useTranslation } from 'react-i18next';
import { Box, Button, TextField } from '@mui/material';
import { borderRadius, palette, spaces } from '../../../assets/styles/themes/tokens';
import { SnackbarContext } from '../../../state/SnackbarContextProvider';

interface FormFields {
  promoCode: string;
}

interface OwnProps {
  isPaymentRythmValid: boolean;
  submittedPromoCode?: string;
  setSubmittedPromoCode: (code: string) => void;
  setDiscountInformation: (discountInformation: DiscountInformation | undefined) => void;
  setFormLayout: (layout: number) => void;
  licenceId: number;
  paymentRhythm: PaymentPeriod;
  onPromoCodeValidation: (discountInformation: DiscountInformation, promoCode: string) => void;
  onActiveLayoutInit: () => void;
  initPromoCode?: string;
}

const PromoCodeInput: React.FC<OwnProps> = (props) => {
  const { t } = useTranslation();
  const { openSnackbar } = useContext(SnackbarContext);
  const [disableButton, setDisableButton] = useState<boolean>(true);

  const {
    handleSubmit,
    register,
    errors,
    setError,
    getValues,
    setValue,
    clearError
  } = useForm<FormFields>({
    mode: 'onChange'
  });

  useEffect(() => {
    if (!props.isPaymentRythmValid) {
      setError('promoCode', 'invalidPaymentRythm', t('This code is invalid for this payment rythm'));
      notifyBugsnagHandledError(
        new Error(
          `Invalid payment rythm selected for promo code! promoCode: ${props.submittedPromoCode},
          paymentRhythm: ${props.paymentRhythm}, licenceId: ${props.licenceId}`
        )
      );
    }
  }, [props.isPaymentRythmValid]);

  useEffect(() => {
    props.onActiveLayoutInit();
    if (props.initPromoCode) {
      setValue('promoCode', props.initPromoCode);
      onUpgradeCodeSubmit();
    }
  }, []);

  const onUpgradeCodeSubmit = handleSubmit(async () => {
    const promoCode = getValues().promoCode;
    try {
      props.setSubmittedPromoCode(promoCode);
      const discountInformation =
        await services.billing.getDiscountInformation(promoCode, props.licenceId);
      if (discountInformation?.valid) {
        props.setDiscountInformation(discountInformation);
        props.setFormLayout(2);
        props.onPromoCodeValidation(discountInformation, promoCode);
        if (!disableButton) {
          openSnackbar({
            type: 'success',
            message: t('The code has been applied!'),
          });
        }
      } else {
        props.setDiscountInformation(undefined);
        setError('promoCode', 'notValid', t('The code is invalid or does not exist — try entering your code again'));
      }
    } catch (error) {
      setError('promoCode', 'notValid', t('The code is invalid or does not exist — try entering your code again'));
      notifyBugsnagHandledError(new Error(String(error)));
    }
  });

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDisableButton(!event.target.value);
    clearError('promoCode');
  };

  return (
    <Box
      data-testid='promo-code-form' 
      display='flex' 
      alignItems='center'
      gap={spaces.xs} 
      component='form' 
      onSubmit={onUpgradeCodeSubmit}
    >
      <TextField
        data-testid='promo-code-input'
        variant='outlined'
        placeholder={t('Enter your code here')}
        size='small'
        sx={{ 
          flex: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius.xs,
          },
        }}
        onChange={onInputChange}
        inputRef={register}
        name='promoCode'
        error={!!errors.promoCode}
        helperText={errors.promoCode?.message}
      />
      <Button
        data-testid='promo-code-submit'
        type='submit' 
        variant='text' 
        size='large' 
        sx={{ color: errors.promoCode ? palette.error.main : palette.secondary.main, alignSelf: 'baseline' }}
        disabled={disableButton}
      >
        {t('Redeem')}
      </Button>
    </Box>
  );
};

export default PromoCodeInput;

